// Mobile tank roster (stationary types excluded) with the per-type FUNCTIONS ported from
// wiiplaytanks. Tanks are colored by TEAM; the type picks the turret shape, the combat stats,
// and — new — a fire MODE + move MODE that drive distinct behavior:
//   direct  bouncing shell, needs line-of-sight               (gray/red/brute/…)
//   rocket  fast straight shell, needs LOS                    (marauder/gatling)
//   sniper  very fast long-range shell, needs LOS, kites      (stalker)
//   arc     lobbed parabolic shell, NO LOS, long range, blasts on impact (artillery = mortar)
//   seek    homing missile, kites                             (tow)
//   ram     no shell; charges the target and detonates        (kamikaze)
//   none    support type, no weapon yet (advances as a body)  (spawner/shieldgen/thrower)

export type FireMode = "direct" | "rocket" | "sniper" | "arc" | "seek" | "ram" | "none";
export type MoveMode = "advance" | "kite" | "charge";

export interface TankFire {
  interval: number; // seconds between shots
  speed: number; // shell speed (studs/s, before BULLET_SCALE); ignored by arc (arc time = dist-based)
  bounces: number; // wall bounces before the shell expires
  shells: number; // max shells alive at once
  aimError: number; // radians of aim jitter
  lead: number; // how much to lead a moving target (0..1)
}

export interface TankTypeDef {
  id: string;
  color: number; // original type color (unused now — kept for reference)
  turretless?: boolean; // hide the turret
  hp: number; // longfight-scaled (source health * 40)
  mode: FireMode;
  move: MoveMode;
  fire?: TankFire; // present for every shooting mode; absent for ram/none
  blastR?: number; // AoE radius (studs) for arc + ram detonations
  rangeMul?: number; // engage-range multiplier (arc/sniper reach far); default 1
}

const F = (interval: number, speed: number, bounces: number, shells: number, aimError: number, lead: number): TankFire => ({
  interval, speed, bounces, shells, aimError, lead,
});

export const TANK_TYPES: TankTypeDef[] = [
  { id: "gray", color: 0x9aa0a6, hp: 40, mode: "direct", move: "advance", fire: F(2.6, 70, 1, 1, 0.14, 0.2) },
  { id: "yellow", color: 0xd8c020, hp: 40, mode: "direct", move: "advance", fire: F(2.2, 80, 1, 1, 0.1, 0.3) },
  { id: "red", color: 0xc83232, hp: 40, mode: "direct", move: "advance", fire: F(1.6, 95, 1, 2, 0.07, 0.45) },
  { id: "purple", color: 0x8a3fcf, hp: 40, mode: "direct", move: "advance", fire: F(1.8, 95, 1, 2, 0.07, 0.5) },
  { id: "white", color: 0xeafaff, hp: 40, mode: "direct", move: "advance", fire: F(2.0, 90, 1, 2, 0.08, 0.4) },
  { id: "black", color: 0x222222, hp: 80, mode: "direct", move: "advance", fire: F(1.3, 120, 1, 3, 0.05, 0.6) },
  { id: "marauder", color: 0xe04010, hp: 80, mode: "rocket", move: "advance", fire: F(2.0, 320, 0, 2, 0.05, 0.55) },
  { id: "gatling", color: 0x4f5a66, hp: 120, mode: "rocket", move: "advance", fire: F(0.16, 290, 0, 6, 0.12, 0.45) },
  { id: "brute", color: 0x7a5cc0, hp: 160, mode: "direct", move: "advance", fire: F(1.9, 95, 1, 2, 0.09, 0.4) },
  // kamikaze: turretless mine on treads — charges the nearest enemy and detonates on contact
  { id: "kamikaze", color: 0xff2e2e, hp: 40, turretless: true, mode: "ram", move: "charge", blastR: 110 },
  // stalker: mobile sniper — very fast, far-reaching bolts; kites to keep its distance
  { id: "stalker", color: 0x2b2f4a, hp: 40, mode: "sniper", move: "kite", fire: F(2.6, 600, 4, 1, 0.0, 0.3), rangeMul: 1.7 },
  // spawner / shieldgen / thrower: support types — no weapon ported yet (advance as bodies)
  { id: "spawner", color: 0x3f9d63, hp: 120, turretless: true, mode: "none", move: "advance" },
  { id: "shieldgen", color: 0x32d6c4, hp: 120, mode: "none", move: "advance" },
  { id: "thrower", color: 0xb8702e, hp: 120, turretless: true, mode: "none", move: "advance" },
  // artillery = MORTAR: lobs arcing explosive shells over walls, no line-of-sight, long range
  { id: "artillery", color: 0xcf7a2a, hp: 80, mode: "arc", move: "advance", fire: F(3.6, 0, 0, 1, 0.05, 0.6), blastR: 150, rangeMul: 2.4 },
  // tow: kites and launches slow heat-seeking missiles
  { id: "tow", color: 0x7a4fb0, hp: 80, mode: "seek", move: "kite", fire: F(3.4, 150, 0, 2, 0.04, 0.4), rangeMul: 1.3 },
  { id: "abomination", color: 0x8b0000, hp: 480, mode: "direct", move: "advance", fire: F(1.6, 110, 1, 2, 0.06, 0.5) },
  { id: "ally", color: 0x2fd0c0, hp: 40, mode: "direct", move: "advance", fire: F(0.9, 128, 1, 3, 0.03, 0.4) },
];
